import { API_URL } from "../_config/config.const";
import { handleResponse } from "./handleResponse.helper";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const CONTAINER_ID = "lq-turnstile";

type TurnstileApi = {
    render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
    execute: (widgetId: string) => void;
    reset: (widgetId: string) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let widgetId: string | null = null;
let hasExecuted = false;
let siteKeyPromise: Promise<string> | null = null;
let tokenQueue: Promise<string> = Promise.resolve("");
let pendingResolve: ((token: string) => void) | null = null;
let pendingReject: ((error: Error) => void) | null = null;

function hideTurnstileContainer(container: HTMLElement) {
    container.style.position = "fixed";
    container.style.left = "0";
    container.style.bottom = "0";
    container.style.width = "1px";
    container.style.height = "1px";
    container.style.overflow = "hidden";
    container.style.opacity = "0";
    container.style.pointerEvents = "none";
    container.style.zIndex = "0";
}

function showTurnstileContainer(container: HTMLElement) {
    container.style.position = "absolute";
    container.style.left = "0";
    container.style.right = "0";
    container.style.bottom = "8px";
    container.style.transform = "none";
    container.style.width = "100%";
    container.style.height = "auto";
    container.style.overflow = "visible";
    container.style.opacity = "1";
    container.style.pointerEvents = "auto";
    container.style.zIndex = "2";
    container.style.display = "flex";
    container.style.justifyContent = "center";
    container.style.marginTop = "0";
}

function ensureContainer(): HTMLElement {
    const existing = document.getElementById(CONTAINER_ID);
    const container = existing ?? document.createElement("div");
    if (!existing) {
        container.id = CONTAINER_ID;
        document.body.appendChild(container);
    }

    hideTurnstileContainer(container);
    return container;
}

function loadTurnstileSiteKey(): Promise<string> {
    if (siteKeyPromise) return siteKeyPromise;

    siteKeyPromise = fetch(`${API_URL}/form/turnstileSiteKey`)
        .then(handleResponse)
        .then((data) => {
            const siteKey = String(data?.siteKey ?? data?.SiteKey ?? "");
            if (!siteKey) throw new Error("Captcha is not configured.");
            return siteKey;
        })
        .catch((err) => {
            siteKeyPromise = null;
            throw err;
        });

    return siteKeyPromise;
}

function loadTurnstileScript(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);

    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`) as HTMLScriptElement | null;
        if (existing) {
            if (window.turnstile) {
                resolve(window.turnstile);
                return;
            }
            existing.addEventListener("load", () => resolveTurnstile(resolve, reject));
            existing.addEventListener("error", () => reject(new Error("Captcha failed to load")));
            return;
        }

        const script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT_SRC;
        script.onload = () => resolveTurnstile(resolve, reject);
        script.onerror = () => reject(new Error("Captcha failed to load"));
        document.head.appendChild(script);
    });
}

function resolveTurnstile(resolve: (api: TurnstileApi) => void, reject: (error: Error) => void) {
    if (window.turnstile) {
        resolve(window.turnstile);
        return;
    }

    reject(new Error("Captcha failed to load"));
}

function rejectPending(message: string) {
    if (pendingReject) pendingReject(new Error(message));
    pendingResolve = null;
    pendingReject = null;
}

function resolvePending(token: string) {
    if (pendingResolve) pendingResolve(token);
    pendingResolve = null;
    pendingReject = null;
}

function renderWidget(turnstile: TurnstileApi, siteKey: string): string {
    if (widgetId) return widgetId;

    widgetId = turnstile.render(ensureContainer(), {
        sitekey: siteKey,
        appearance: "interaction-only",
        execution: "execute",
        size: "flexible",
        callback: (token: string) => {
            hideTurnstileContainer(ensureContainer());
            resolvePending(token);
        },
        "before-interactive-callback": () => showTurnstileContainer(ensureContainer()),
        "error-callback": () => rejectPending("Captcha validation failed."),
        "expired-callback": () => rejectPending("Captcha expired. Please try again."),
        "timeout-callback": () => rejectPending("Captcha timed out. Please try again.")
    });

    return widgetId;
}

function requestNewToken(): Promise<string> {
    return loadTurnstileSiteKey().then((siteKey) => {
        return loadTurnstileScript().then((turnstile) => {
            return new Promise<string>((resolve, reject) => {
                pendingResolve = resolve;
                pendingReject = reject;
                const id = renderWidget(turnstile, siteKey);
                if (hasExecuted) turnstile.reset(id);
                hasExecuted = true;
                turnstile.execute(id);
            });
        });
    });
}

export function getTurnstileToken(): Promise<string> {
    tokenQueue = tokenQueue.then(() => requestNewToken(), () => requestNewToken());
    return tokenQueue;
}
